import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIsMutating } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader,
  passwordManagerIgnoreProps
} from '@bliks-io/ui';
import {
  PersonioCredentialsSchema,
  type DataSyncStatus,
  type PersonioCredentials
} from '@bliks-io/contracts';
import { FormInput } from '@/components/form/rhf';
import { useDataSyncStatus } from '@/api/queries/useDataSync';
import { useLinkPersonio, useTestPersonioConnection } from '@/api/mutations/useDataSync';
import { translateErrorCode } from '@/lib/errors/error-formatter';
import { isForbiddenError } from '@/api/errors';
import { dataSyncKeys } from '@/api/query-keys';

interface PersonioLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLinked?: (status: DataSyncStatus) => void;
}

/** Links Personio as the data source with its API credentials, or replaces them. */
export function PersonioLinkDialog({ open, onOpenChange, onLinked }: PersonioLinkDialogProps) {
  // A link closed mid-request would still link, and its error would reach no one.
  const isLinking = useIsMutating({ mutationKey: dataSyncKeys.linkPersonio() }) > 0;
  const changeOpen = (next: boolean) => {
    if (next || !isLinking) onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogShell size="md">
        {/* Radix unmounts the content while closed, so every open starts with an
            empty form and no test result. */}
        <PersonioLinkForm
          onClose={() => changeOpen(false)}
          onLinked={(status) => {
            onLinked?.(status);
            onOpenChange(false);
          }}
        />
      </DialogShell>
    </Dialog>
  );
}

function PersonioLinkForm({
  onClose,
  onLinked
}: {
  onClose: () => void;
  onLinked: (status: DataSyncStatus) => void;
}) {
  const { t } = useTranslation();
  const { data: status } = useDataSyncStatus();
  // Read once on mount: a first link flips the status while the dialog animates out.
  const [replacing] = useState(() => !!status?.personio);
  const link = useLinkPersonio();
  const test = useTestPersonioConnection();
  const isPending = test.isPending || link.isPending;
  const { reset: resetTest } = test;
  const { reset: resetLink } = link;

  const form = useForm<PersonioCredentials>({
    resolver: zodResolver(PersonioCredentialsSchema),
    defaultValues: { clientId: '', clientSecret: '' }
  });

  useEffect(
    function clearFeedbackOnEdit() {
      const subscription = form.watch(() => {
        resetTest();
        resetLink();
      });
      return () => subscription.unsubscribe();
    },
    [form, resetTest, resetLink]
  );

  const submitWith = (run: (credentials: PersonioCredentials) => void) =>
    form.handleSubmit((credentials) => {
      resetTest();
      resetLink();
      run(credentials);
    });
  const testConnection = submitWith((credentials) => test.mutate(credentials));
  const connect = submitWith((credentials) => link.mutate(credentials, { onSuccess: onLinked }));

  // The HTTP client already handles every 403.
  const error = link.error ?? test.error;
  const errorMessage =
    error && !isForbiddenError(error) ? translateErrorCode(error, 'errors.unknown', t) : null;

  const credentialFieldProps = {
    control: form.control,
    disabled: isPending,
    required: true,
    // Password managers would offer to fill a saved login into these or to store the secret.
    ...passwordManagerIgnoreProps
  };

  return (
    <>
      <DialogShellHeader
        title={t(replacing ? 'dataSync.personio.replaceTitle' : 'dataSync.personio.title')}
        onClose={onClose}
      />
      <form noValidate onSubmit={connect} className="flex min-h-0 flex-1 flex-col">
        <DialogShellBody>
          <div className="flex flex-col gap-4">
            <PersonioInstructions />
            <FormInput
              {...credentialFieldProps}
              name="clientId"
              label={t('dataSync.personio.clientId')}
            />
            <FormInput
              {...credentialFieldProps}
              name="clientSecret"
              type="password"
              autoComplete="new-password"
              label={t('dataSync.personio.clientSecret')}
            />
            {errorMessage && (
              <p role="alert" className="text-sm text-danger">
                {errorMessage}
              </p>
            )}
            {test.isSuccess && (
              <p role="status" className="text-sm text-positive">
                {t('dataSync.personio.testSucceeded')}
              </p>
            )}
          </div>
        </DialogShellBody>
        <DialogShellFooter
          leftSlot={
            <Button
              type="button"
              variant="secondary"
              loading={test.isPending}
              disabled={link.isPending}
              onClick={() => void testConnection()}
            >
              {t('dataSync.personio.test')}
            </Button>
          }
          secondary={{ label: t('common.cancel'), onClick: onClose, disabled: link.isPending }}
          primary={{
            label: t(replacing ? 'common.save' : 'dataSync.personio.connect'),
            type: 'submit',
            loading: link.isPending,
            disabled: test.isPending
          }}
        />
      </form>
    </>
  );
}

const INSTRUCTION_STEPS = ['step1', 'step2', 'step3', 'step3a', 'step4', 'step5'] as const;

/** Where the admin creates the credentials in Personio, and which permissions Bliks reads with. */
function PersonioInstructions() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 text-sm text-fg-secondary">
      <ol className="flex list-decimal flex-col gap-1.5 pl-5">
        {INSTRUCTION_STEPS.map((step) => (
          <li key={step}>
            <Trans
              i18nKey={`dataSync.personio.instructions.${step}`}
              components={{ bold: <strong className="font-semibold text-foreground" /> }}
            />
          </li>
        ))}
      </ol>
      <p>{t('dataSync.personio.instructions.readOnlyNote')}</p>
      <a
        href={t('dataSync.personio.instructions.helpUrl')}
        target="_blank"
        rel="noopener noreferrer"
        className="self-start text-info hover:underline"
      >
        {t('dataSync.personio.instructions.helpLink')}
      </a>
    </div>
  );
}
